import { Link, usePage } from "@inertiajs/react"
import {
  BookOpen,
  Gift,
  ListTodo,
  type LucideIcon,
  User,
  Users,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { CREATOR_APP_COLUMN } from "@/lib/creator-layout"
import { cn } from "@/lib/utils"
import {
  creatorCrewPath,
  creatorMissionsPath,
  creatorProfilePath,
  creatorRewardsPath,
  learnMasterclassesPath,
} from "@/routes"

/**
 * OKS-385 (TRD KD-1/KD-2) — config-driven bottom-tab nav, EXTRACTED VERBATIM
 * from the shipped `creator-shell` `CreatorBottomNav`. This is a pure
 * refactor: the same testids (`${navTestId}`, `${itemTestIdPrefix}-${key}`),
 * the same `aria-current="page"` semantics, the same `match` fns, and the
 * same disabled-slot treatment are preserved so existing creator-nav specs
 * (Vitest / system / Playwright) stay green.
 *
 * The component is now generic: any surface can supply its own `items`
 * config + `labelNamespace`. V1 ships the creator 5-slot config (all slots
 * enabled — the OKS-7/K2 "Rank/Learn disabled Soon" framing is stale since
 * OKS-51/OKS-56 enabled both), but the `enabled: false` disabled-slot path is
 * retained so a future config (e.g. the Figma 4-tab variant) can land without
 * a rewrite.
 *
 * Labels resolve via the `${labelNamespace}.${labelKey ?? key}` template so
 * the static-prefix i18n health check behaves exactly as before the extract.
 */
export interface TabItem {
  /** Stable key — react key + data-testid (`${itemTestIdPrefix}-${key}`) +
   *  DEFAULT label lookup (`${labelNamespace}.${key}`). */
  key: string
  /** Optional label-key override; when set, the label resolves via
   *  `${labelNamespace}.${labelKey}` while testid/match stay on `key`. */
  labelKey?: string
  /** Destination; `null` for disabled slots. */
  href: string | null
  icon: LucideIcon
  /** Drives `aria-current="page"` + active styling. */
  match: (url: string) => boolean
  /** `false` → rendered as a non-focusable "Soon" slot. */
  enabled: boolean
  /**
   * OKS-484 — optional count badge on an enabled slot (e.g. Missions "N to
   * submit"). `> 0` renders a small count `Badge` (display capped `9+`);
   * `0`/`undefined` → no badge. testid `${itemTestIdPrefix}-${key}-count`.
   */
  badgeCount?: number
}

interface TabBarProps {
  items: TabItem[]
  /**
   * i18n namespace prefix. Drives every key: item labels
   * (`${labelNamespace}.${labelKey ?? key}`), the nav `aria_label`, and the
   * disabled-slot `coming_soon` / `coming_soon_suffix` copy.
   */
  labelNamespace: string
  /** `data-testid` on the `<nav>` element. */
  navTestId: string
  /** Prefix for per-item `data-testid` (`${itemTestIdPrefix}-${key}`). */
  itemTestIdPrefix: string
  className?: string
}

/**
 * TabBar — fixed bottom-tab navigation.
 *
 * OKS-739 — visible at ALL widths (was `lg:hidden`). The creator app has no
 * desktop layout, so the bottom-tab shell is the primary nav on every width
 * including iPad (≥1024px). Visibility is now a caller concern: a future
 * surface wanting a breakpoint hide passes it via `className`.
 *
 * The bar chrome (`fixed inset-x-0` background + top border) stays full-bleed
 * at every width; on wide screens (`lg+`) only the TAB ROW centers to the
 * shared `CREATOR_APP_COLUMN`, so the tabs sit under the centered content
 * column instead of scattering across a huge width (TRD KD-3).
 */
export function TabBar({
  items,
  labelNamespace,
  navTestId,
  itemTestIdPrefix,
  className,
}: TabBarProps) {
  const { t } = useTranslation()
  const { url } = usePage()

  return (
    <nav
      aria-label={t(`${labelNamespace}.aria_label`)}
      data-testid={navTestId}
      className={cn(
        // OKS-638 — `viewport-fit=cover` (application.html.erb) lets this
        // `fixed bottom-0` bar draw under the iOS home indicator.
        // OKS-702 — the total nav height is `4rem + env(safe-area-inset-bottom)`
        // and `pb-[env(safe-area-inset-bottom)]` reserves the inset BELOW the
        // row. With border-box, the content box resolves to exactly 4rem (the
        // 64px interactive row) regardless of inset, so `items-stretch` fills a
        // full-height row on home-indicator devices instead of the inset eating
        // it. In-browser the inset is 0 → total height 4rem, visually unchanged.
        // OKS-739 — `lg:hidden` removed; the bar persists at all widths.
        "border-border/60 bg-background/95 supports-[backdrop-filter]:bg-background/85 fixed inset-x-0 bottom-0 z-20 flex h-[calc(4rem+env(safe-area-inset-bottom))] items-stretch border-t pb-[env(safe-area-inset-bottom)] backdrop-blur",
        className,
      )}
    >
      {/* OKS-739 — inner row centers the tabs to the shared app-column at
          `lg+` (`CREATOR_APP_COLUMN`) while the `<nav>` background/border stay
          full-bleed. Below `lg` this is a plain full-width flex row (inert
          `lg:` utilities) = byte-identical to pre-OKS-739. */}
      <div
        className={cn("flex h-full w-full items-stretch", CREATOR_APP_COLUMN)}
      >
        {items.map((tab) => {
          if (!tab.enabled || tab.href === null) {
            return (
              <DisabledTab
                key={tab.key}
                tab={tab}
                labelNamespace={labelNamespace}
                itemTestIdPrefix={itemTestIdPrefix}
              />
            )
          }
          const isActive = tab.match(url)
          const title = t(`${labelNamespace}.${tab.labelKey ?? tab.key}`)
          const badgeCount = tab.badgeCount ?? 0
          return (
            <Link
              key={tab.key}
              href={tab.href}
              aria-current={isActive ? "page" : undefined}
              data-testid={`${itemTestIdPrefix}-${tab.key}`}
              className={cn(
                "relative flex flex-1 flex-col items-center justify-center gap-0.5 text-xs font-medium",
                // 56px nav slot height via `h-16` on parent; min 44px tap target
                // is satisfied by the flex-1 full-slot click area.
                isActive ? "text-foreground" : "text-muted-foreground",
              )}
            >
              <tab.icon className="size-5" aria-hidden="true" />
              <span>{title}</span>
              {badgeCount > 0 && (
                <Badge
                  data-testid={`${itemTestIdPrefix}-${tab.key}-count`}
                  className="absolute top-1 right-[calc(50%-1.25rem)] flex h-4 min-w-4 items-center justify-center rounded-full px-1 py-0 text-[9px] leading-none tabular-nums"
                >
                  {badgeCount > 9 ? "9+" : badgeCount}
                </Badge>
              )}
            </Link>
          )
        })}
      </div>
    </nav>
  )
}

/**
 * DisabledTab — disabled-slot treatment (preserved from the OKS-7 K2 impl).
 *
 * Renders a native `<button disabled>` so:
 *   - Tab order skips it (disabled buttons are not focusable by default)
 *   - `aria-disabled` explicitly marks it for assistive tech
 *   - `tabIndex={-1}` is defensive belt-and-braces for older AT
 *   - `pointer-events: none` prevents hover/click entirely
 *   - A visible "Soon" pill signals future-ness
 *   - An `sr-only` suffix extends the label for screen readers
 *
 * No item in the V1 creator config uses this path (all 5 slots are enabled),
 * but it is retained so a future disabled-slot config works unchanged.
 */
function DisabledTab({
  tab,
  labelNamespace,
  itemTestIdPrefix,
}: {
  tab: TabItem
  labelNamespace: string
  itemTestIdPrefix: string
}) {
  const { t } = useTranslation()
  const title = t(`${labelNamespace}.${tab.labelKey ?? tab.key}`)
  return (
    <button
      type="button"
      disabled
      aria-disabled="true"
      tabIndex={-1}
      data-testid={`${itemTestIdPrefix}-${tab.key}`}
      data-disabled="true"
      className="text-muted-foreground pointer-events-none relative flex flex-1 flex-col items-center justify-center gap-0.5 text-xs font-medium opacity-80"
    >
      <tab.icon className="size-5" aria-hidden="true" />
      <span>{title}</span>
      <Badge
        variant="secondary"
        aria-hidden="true"
        className="absolute top-1 right-2 h-3.5 rounded-full px-1.5 py-0 text-[9px] leading-none"
      >
        {t(`${labelNamespace}.coming_soon`)}
      </Badge>
      <span className="sr-only">
        {t(`${labelNamespace}.coming_soon_suffix`)}
      </span>
    </button>
  )
}

/**
 * CREATOR_TABS builder — the creator bottom-tab config: **Missions / Rewards /
 * Crew / Learn* / Profile** (4 slots without Learn, 5 with). OKS-570 slimmed
 * the nav from Home / Missions / Crew / Rank→Leaderboard / Learn / Profile;
 * OKS-864 then promoted Rewards into slot 2:
 *   - The `home` slot is GONE — it duplicated Missions (both → `/missions`).
 *     Missions is now the leftmost / home tab and ABSORBS Home's match union
 *     (the legacy `/`, `/dashboard`, and `/missions*` aliases), so exactly one
 *     slot carries `aria-current` on the home (no more K1 dual-active).
 *   - The `rank` slot is GONE — redundant now that Crew is a tab. The
 *     `/leaderboard` PAGE stays reachable via the Crew hub "This Week's
 *     Leaders" card link (OKS-570 PRD OQ-2), NOT a tab.
 *   - `rewards` (OKS-864) sits at slot 2, between Missions and Crew, and is
 *     inserted UPSTREAM of the conditional Learn spread so Profile stays
 *     rightmost in both layouts. Icon `Gift` (not `Coins` — that would double
 *     the header `CoinsPill`'s glyph); the pill stays mounted and clickable,
 *     so two entry points to `/rewards` coexist by design.
 *   - `learn` is now CONDITIONAL: pushed only when the active brand has ≥1
 *     published masterclass (`hasMasterclassContent`). When false it is fully
 *     OMITTED (not a disabled "Soon" slot) — there is genuinely nothing to
 *     show. Sits between Crew and Profile so Profile stays rightmost whether
 *     Learn is present or not.
 *
 * Evaluated at render time (not module top-level) so route helpers that read
 * runtime state stay safe.
 *
 * @param toSubmitCount OKS-484 — "N to submit" count for the Missions slot
 *   badge. Defaults to 0 (no badge). Sourced from the `to_submit_count` shared
 *   prop via `useCurrentContext().toSubmitCount` in the creator shell.
 * @param hasMasterclassContent OKS-570 — Learn-slot gate. `true` iff the
 *   active brand has ≥1 published masterclass (creator-only, brand-scoped
 *   `has_masterclass_content` shared prop via `useCurrentContext()`). `false`
 *   (the default) → the Learn slot is omitted.
 */
export function buildCreatorTabs(
  toSubmitCount = 0,
  hasMasterclassContent = false,
): TabItem[] {
  const missionsHref = creatorMissionsPath()
  return [
    {
      key: "missions",
      href: missionsHref,
      icon: ListTodo,
      // OKS-570 — Missions is the home tab; it absorbs the old Home match
      // union (`/`, `/dashboard`, `/missions*`). The backend dispatcher 303s
      // `/dashboard` → `/missions`, so all aliases resolve to this slot.
      match: (url) =>
        url === missionsHref ||
        url === "/" ||
        url.startsWith("/missions") ||
        url === "/dashboard",
      enabled: true,
      // OKS-484 (A) — "N to submit" badge (accepted/delivered zero-submission
      // collabs in the active brand). 0 → no badge.
      badgeCount: toSubmitCount,
    },
    {
      // OKS-864 — Rewards promoted to a first-class slot (was reachable only
      // via the header `CoinsPill`, which stays mounted + clickable — two
      // entry points to `/rewards` coexist by design). Slot 2, upstream of
      // the Learn spread so order is Missions·Rewards·Crew·Learn*·Profile.
      // Icon `Gift` (not `Coins` — that would double the pill's glyph). Match
      // mirrors the `learn` shape: also active on `/rewards/mine` (redemption
      // history). Audited clean — no other tab matches `/rewards*`.
      key: "rewards",
      href: creatorRewardsPath(),
      icon: Gift,
      match: (url) =>
        url === "/rewards" ||
        url.startsWith("/rewards/") ||
        url.startsWith("/rewards?"),
      enabled: true,
    },
    {
      // OKS-473 — §06 Crew tab. Mounted at `/my-crew` (NOT `/crew` — that's
      // the shipped brand roster); active on `/my-crew` AND the
      // `/my-crew/pulse` drill-down.
      key: "crew",
      href: creatorCrewPath(),
      icon: Users,
      match: (url) => url === "/my-crew" || url.startsWith("/my-crew"),
      enabled: true,
    },
    // OKS-56 / OKS-570 — Learn slot → creator-side training (`/learn`). KEY
    // stays "learn" (data-testid `creator-tab-learn`). Conditionally included:
    // only pushed when the active brand has published masterclass content.
    ...(hasMasterclassContent
      ? [
          {
            key: "learn",
            href: learnMasterclassesPath(),
            icon: BookOpen,
            match: (url: string) =>
              url === "/learn" ||
              url.startsWith("/learn/") ||
              url.startsWith("/learn?"),
            enabled: true,
          } satisfies TabItem,
        ]
      : []),
    {
      key: "profile",
      // OKS-48 — creator-only `/profile`; also active on legacy `/settings/*`
      // (reachable via the HeaderNavUser dropdown) so the section reads
      // identically.
      href: creatorProfilePath(),
      icon: User,
      match: (url) => url === "/profile" || url.startsWith("/settings"),
      enabled: true,
    },
  ]
}
